import React, { useState, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useAuth } from '../../auth/AuthContext';
import { apiFetch } from '../../api/client';
import { invalidateAndRefetch, QUERY_KEYS } from '../../api/queryCache';

// ── Types ─────────────────────────────────────────────────────

interface TotpStatus {
  enabled: boolean;
  provisionedAt: string | null;
  remainingRecoveryCodes: number;
}

interface SetupInitResponse {
  secret: string;
  qrCodeDataUri: string;
  otpAuthUri: string;
}

interface SetupConfirmResponse {
  recoveryCodes: string[];
  message: string;
}

type SetupStep = 'idle' | 'scan' | 'verify' | 'codes' | 'disable';

// ── Shared inline styles (theme-token–based) ──────────────────

const ST = {
  card: {
    padding: '14px 16px', borderRadius: 8, marginBottom: 10,
    background: 'var(--bg-hover)', border: '1px solid var(--border-subtle)',
  } as React.CSSProperties,
  title: {
    fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 2,
  } as React.CSSProperties,
  desc: {
    fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.35,
  } as React.CSSProperties,
  badge: (enabled: boolean) => ({
    display: 'inline-block', fontSize: 10, fontWeight: 600,
    padding: '2px 8px', borderRadius: 10,
    background: enabled ? 'var(--color-success)' : 'var(--bg-surface)',
    color: enabled ? '#fff' : 'var(--text-muted)',
    marginInlineStart: 8,
  } as React.CSSProperties),
  btn: {
    padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, background: 'var(--accent-blue)', color: '#fff',
  } as React.CSSProperties,
  btnDanger: {
    padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, background: 'var(--color-danger)', color: '#fff',
  } as React.CSSProperties,
  btnSecondary: {
    padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
    fontSize: 12, fontWeight: 600, background: 'transparent',
    color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)',
  } as React.CSSProperties,
  input: {
    padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border-default)',
    background: 'var(--bg-input)', color: 'var(--text-primary)',
    fontSize: 18, fontFamily: 'monospace', letterSpacing: 6, textAlign: 'center' as const,
    width: 180,
  } as React.CSSProperties,
  codeBox: {
    padding: 12, borderRadius: 8, background: 'var(--bg-surface)',
    border: '1px solid var(--border-default)', fontFamily: 'monospace',
    fontSize: 13, lineHeight: 2.2, textAlign: 'center' as const,
    userSelect: 'all' as const,
  } as React.CSSProperties,
  error: {
    fontSize: 11, color: 'var(--color-danger)', marginTop: 4,
  } as React.CSSProperties,
  notice: {
    fontSize: 11, color: 'var(--text-muted)', marginTop: 6, fontStyle: 'italic' as const,
  } as React.CSSProperties,
  stepTitle: {
    fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6,
  } as React.CSSProperties,
  stepDesc: {
    fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.4,
  } as React.CSSProperties,
};

// ── Component ─────────────────────────────────────────────────

interface TotpSetupProps {
  isSystemAdmin: boolean;
}

const TotpSetup: React.FC<TotpSetupProps> = ({ isSystemAdmin }) => {
  const { t } = useAppTranslation('userProfile');

  // ── Local state ───────────────────────────────────────
  const [status, setStatus] = useState<TotpStatus | null>(null);
  const [step, setStep] = useState<SetupStep>('idle');
  const [setupData, setSetupData] = useState<SetupInitResponse | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // ── Load status on mount ──────────────────────────────
  const loadStatus = useCallback(async () => {
    try {
      const data = await apiFetch<TotpStatus>('/api/totp/status');
      setStatus(data);
    } catch (err) {
      console.warn('[TotpSetup] Failed to load TOTP status:', err);
      // Component will show setup button as fallback
    }
  }, []);

  React.useEffect(() => { loadStatus(); }, [loadStatus]);

  // ── Begin setup ───────────────────────────────────────
  const handleBeginSetup = async () => {
    setError('');
    setLoading(true);
    try {
      const data = await apiFetch<SetupInitResponse>('/api/totp/setup/initialize', {
        method: 'POST',
      });
      setSetupData(data);
      setStep('scan');
    } catch (e: any) {
      setError(e?.message || 'Failed to initialize setup');
    } finally {
      setLoading(false);
    }
  };

  // ── Advance to verify step ────────────────────────────
  const handleScanNext = () => {
    setCode('');
    setError('');
    setStep('verify');
  };

  // ── Confirm setup with TOTP code ──────────────────────
  const handleConfirmSetup = async () => {
    if (code.length !== 6) { setError('Enter a 6-digit code.'); return; }
    setError('');
    setLoading(true);
    try {
      const data = await apiFetch<SetupConfirmResponse>('/api/totp/setup/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret: setupData!.secret, code }),
      });
      setRecoveryCodes(data.recoveryCodes);
      setStep('codes');
      // Refresh local status
      await loadStatus();
      // DEV-906 — invalidate the security users cache so the synthetic
      // milestone evaluator picks up the new is_two_factor_enabled
      // flag, clearing the enable_2fa task in Tasks tab + Validation
      // panel without a manual reload.
      invalidateAndRefetch(QUERY_KEYS.security);
    } catch (e: any) {
      setError(e?.message || 'Verification failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Copy recovery codes ───────────────────────────────
  const handleCopyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard API not available */ }
  };

  // ── Disable 2FA ───────────────────────────────────────
  const handleDisable = async () => {
    if (code.length !== 6) { setError('Enter your current authenticator code.'); return; }
    setError('');
    setLoading(true);
    try {
      await apiFetch('/api/totp/disable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      setStep('idle');
      setCode('');
      await loadStatus();
      // Same security-cache invalidation as enable — so the milestone
      // re-surfaces if the user disables 2FA after having enabled it.
      invalidateAndRefetch(QUERY_KEYS.security);
    } catch (e: any) {
      setError(e?.message || 'Failed to disable 2FA.');
    } finally {
      setLoading(false);
    }
  };

  // ── Render: idle (status overview) ────────────────────
  if (step === 'idle') {
    return (
      <div style={ST.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={ST.title}>
              {t('privacy.twoFactorTitle')}
              <span style={ST.badge(!!status?.enabled)}>
                {status?.enabled ? t('privacy.twoFactorEnabled') : t('privacy.twoFactorDisabled')}
              </span>
            </div>
            <div style={ST.desc}>{t('privacy.twoFactorDesc')}</div>
          </div>
          <div>
            {status?.enabled ? (
              <button
                style={ST.btnDanger}
                onClick={() => { setStep('disable'); setCode(''); setError(''); }}
              >
                {t('privacy.twoFactorDisableBtn')}
              </button>
            ) : (
              <button style={ST.btn} onClick={handleBeginSetup} disabled={loading}>
                {t('privacy.twoFactorSetup')}
              </button>
            )}
          </div>
        </div>
        {status?.enabled && (
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
            {t('privacy.twoFactorRemainingCodes', { count: status.remainingRecoveryCodes })}
          </div>
        )}
        <div style={ST.notice}>
          {isSystemAdmin
            ? t('privacy.twoFactorAdminNotice')
            : t('privacy.twoFactorUserNotice')}
        </div>
        {error && <div style={ST.error}>{error}</div>}
      </div>
    );
  }

  // ── Render: scan QR code ──────────────────────────────
  if (step === 'scan' && setupData) {
    return (
      <div style={ST.card}>
        <div style={ST.stepTitle}>{t('privacy.setupStep1Title')}</div>
        <div style={ST.stepDesc}>{t('privacy.setupStep1Desc')}</div>
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <img
            src={setupData.qrCodeDataUri}
            alt="TOTP QR Code"
            style={{ width: 200, height: 200, borderRadius: 8, background: '#fff', padding: 8 }}
          />
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 8 }}>
          {t('privacy.setupStep1Manual')}
        </div>
        <div style={{
          fontFamily: 'monospace', fontSize: 12, letterSpacing: 2,
          padding: '6px 10px', borderRadius: 6, background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)', textAlign: 'center',
          wordBreak: 'break-all', userSelect: 'all',
        }}>
          {setupData.secret}
        </div>
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={ST.btnSecondary} onClick={() => { setStep('idle'); setSetupData(null); }}>
            {t('common:cancel') || 'Cancel'}
          </button>
          <button style={ST.btn} onClick={handleScanNext}>
            {t('common:next') || 'Next'}
          </button>
        </div>
      </div>
    );
  }

  // ── Render: enter verification code ───────────────────
  if (step === 'verify') {
    return (
      <div style={ST.card}>
        <div style={ST.stepTitle}>{t('privacy.setupStep2Title')}</div>
        <div style={ST.stepDesc}>{t('privacy.setupStep2Desc')}</div>
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder={t('privacy.setupStep2Placeholder')}
            style={ST.input}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmSetup(); }}
          />
        </div>
        {error && <div style={{ ...ST.error, textAlign: 'center' }}>{error}</div>}
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={ST.btnSecondary} onClick={() => setStep('scan')}>
            {t('common:back') || 'Back'}
          </button>
          <button style={ST.btn} onClick={handleConfirmSetup} disabled={loading || code.length !== 6}>
            {t('privacy.setupStep2Verify')}
          </button>
        </div>
      </div>
    );
  }

  // ── Render: recovery codes ────────────────────────────
  if (step === 'codes') {
    return (
      <div style={ST.card}>
        <div style={ST.stepTitle}>{t('privacy.setupStep3Title')}</div>
        <div style={ST.stepDesc}>{t('privacy.setupStep3Desc')}</div>
        <div style={{
          padding: '4px 0', marginBottom: 8,
          fontSize: 11, fontWeight: 600, color: 'var(--color-danger)',
        }}>
          ⚠ {t('privacy.setupStep3Warning')}
        </div>
        <div style={ST.codeBox}>
          {recoveryCodes.map((c, i) => (
            <span key={i}>
              {c}{i < recoveryCodes.length - 1 ? '   ' : ''}
            </span>
          ))}
        </div>
        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center', gap: 10 }}>
          <button style={ST.btnSecondary} onClick={handleCopyCodes}>
            {copied ? t('privacy.setupStep3Copied') : t('privacy.setupStep3Copy')}
          </button>
        </div>
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
          <button style={ST.btn} onClick={() => { setStep('idle'); setRecoveryCodes([]); }}>
            {t('privacy.setupStep3Done')}
          </button>
        </div>
      </div>
    );
  }

  // ── Render: disable confirmation ──────────────────────
  if (step === 'disable') {
    return (
      <div style={ST.card}>
        <div style={ST.stepTitle}>{t('privacy.disableConfirmTitle')}</div>
        <div style={ST.stepDesc}>{t('privacy.disableConfirmDesc')}</div>
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder={t('privacy.setupStep2Placeholder')}
            style={ST.input}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') handleDisable(); }}
          />
        </div>
        {error && <div style={{ ...ST.error, textAlign: 'center' }}>{error}</div>}
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={ST.btnSecondary} onClick={() => { setStep('idle'); setCode(''); setError(''); }}>
            {t('common:cancel') || 'Cancel'}
          </button>
          <button style={ST.btnDanger} onClick={handleDisable} disabled={loading || code.length !== 6}>
            {t('privacy.disableConfirmBtn')}
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default TotpSetup;
